import { DomainValue, DomainValues, FormDefinition } from '../../services/api.service';
import {
  configurationDb,
  DomainValueEntry,
  DomainValuesEntry,
  FormDefinitionEntry,
  EntityEntry,
  UserGroupEntry,
  UserEntry,
} from './databases/configuration-db';
import { UserGroup } from '../users/models/user-group.model';
import { Injectable } from '@angular/core';
import { LoggingService } from '../../services/logging.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  constructor(private readonly logger: LoggingService) {}

  public populateFormDefinitions(formDefinitions: Array<FormDefinition>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!formDefinitions || formDefinitions.length === 0) {
        this.logger.warn('no formDefinitions provided');
        resolve();
      }
      // delete all existing to replace by provided array
      configurationDb.formDefinitions
        .toCollection()
        .delete()
        .then((deleteCount) => {
          this.logger.info('deleted ' + deleteCount + ' formDefinition objects');
          const dateAdded = Date.now();
          const formDefinitionEntries: Array<FormDefinitionEntry> = [];
          for (const formDef of formDefinitions) {
            formDefinitionEntries.push({
              _id: formDef._id,
              timestamp: dateAdded,
              content: formDef,
            });
          }
          configurationDb.formDefinitions
            .bulkPut(formDefinitionEntries)
            .then(() => {
              this.logger.info('populated formDefinitions', formDefinitionEntries.length);
              resolve();
            })
            .catch((error) => {
              this.logger.error(error, false);
              reject(error);
            });
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public populateDomainValues(domainValues: Array<DomainValues>): void {
    if (!domainValues || domainValues.length === 0) {
      this.logger.warn('no domainValues provided');
      return;
    }
    // delete all existing to replace by provided array
    configurationDb.domainValues
      .toCollection()
      .delete()
      .then((deleteCount) => {
        this.logger.info('deleted ' + deleteCount + ' domainValues objects');
        const dateAdded = Date.now();
        const domainValuesEntries: Array<DomainValuesEntry> = [];
        for (const domainVal of domainValues) {
          domainValuesEntries.push({
            _id: domainVal._id,
            timestamp: dateAdded,
            content: domainVal,
          });
        }
        configurationDb.domainValues
          .bulkPut(domainValuesEntries)
          .then(() => {
            this.logger.info('populated domainValues', domainValuesEntries.length);
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  public populateDomainValue(domainValueList: Array<DomainValue>): void {
    if (!domainValueList || domainValueList.length === 0) {
      this.logger.warn('no domainValueList provided');
      return;
    }
    const domainValueName = domainValueList[0].domainValuesName;
    // get all existing to merge with provided array
    configurationDb.domainValue
      .toArray()
      .then((entries) => {
        const dateAdded = Date.now();
        for (const domainValue of domainValueList) {
          const domainValueEntry =
            domainValueName === 'countries'
              ? ConfigurationService.getCorrespondingCountryDomainValueEntry(domainValue, entries)
              : ConfigurationService.getCorrespondingDomainValueEntry(domainValue, entries);
          if (domainValueEntry) {
            configurationDb.domainValue
              .update(domainValueEntry._id, {
                timestamp: dateAdded,
                content: domainValue,
              })
              .then(() => {
                //
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          } else {
            configurationDb.domainValue
              .add({
                _id: domainValue._id,
                timestamp: dateAdded,
                content: domainValue,
              })
              .then(() => {
                this.logger.info('added domainValue');
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private static getCorrespondingDomainValueEntry(
    domainValue: DomainValue,
    domainValueEntries: DomainValueEntry[],
  ): DomainValueEntry | undefined {
    return domainValueEntries.find((x) => x._id === domainValue._id);
  }

  private static getCorrespondingCountryDomainValueEntry(
    domainValue: DomainValue,
    domainValueEntries: DomainValueEntry[],
  ): DomainValueEntry | undefined {
    return domainValueEntries.find(
      (x) => x.content.domainValuesName === domainValue.domainValuesName && x.content.value === domainValue.value,
    );
  }

  public populateEntities(entities: Array<any>): void {
    if (!entities || entities.length === 0) {
      this.logger.warn('no entities provided');
      return;
    }
    // delete all existing to replace by provided array
    configurationDb.entities
      .toCollection()
      .delete()
      .then((deleteCount) => {
        this.logger.info('deleted ' + deleteCount + ' entity objects');
        const dateAdded = Date.now();
        const entityEntries: Array<EntityEntry> = [];
        for (const entity of entities) {
          if (entity.archived) {
            continue;
          }
          entityEntries.push({
            code: entity.code,
            timestamp: dateAdded,
            content: entity,
          });
        }
        configurationDb.entities
          .bulkPut(entityEntries)
          .then(() => {
            this.logger.info('populated entities', entities.length);
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  public populateUserGroups(userGroups: Array<UserGroup>): void {
    if (!userGroups || userGroups.length === 0) {
      this.logger.warn('no userGroups provided');
      return;
    }
    // delete all existing to replace by provided array
    configurationDb.userGroups
      .toCollection()
      .delete()
      .then((deleteCount) => {
        this.logger.info('deleted ' + deleteCount + ' userGroup objects');
        const dateAdded = Date.now();
        const userGroupEntries: Array<UserGroupEntry> = [];
        for (const userGroup of userGroups) {
          userGroupEntries.push({
            groupName: userGroup.GroupName,
            timestamp: dateAdded,
            content: userGroup,
          });
        }
        configurationDb.userGroups
          .bulkPut(userGroupEntries)
          .then(() => {
            this.logger.info('populated userGroups', userGroups.length);
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  public getFormDefinitions(): Promise<FormDefinition[]> {
    return new Promise<FormDefinition[]>((resolve, reject) => {
      const formDefinitions: FormDefinition[] = [];
      configurationDb.formDefinitions
        .toArray()
        .then((entries) => {
          entries.sort((a, b) => {
            if (a.content.order > b.content.order) return 1;
            if (a.content.order < b.content.order) return -1;
            return 0;
          });
          for (const entry of entries) {
            formDefinitions.push(entry.content);
          }
          resolve(formDefinitions);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getFormDefinition(id: string): Promise<FormDefinition> {
    return new Promise<FormDefinition>((resolve, reject) => {
      return configurationDb.formDefinitions
        .get(id)
        .then((result) => {
          if (result) {
            resolve(result.content);
          } else {
            reject('Not found');
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getDomainValues(): Promise<DomainValues[]> {
    return new Promise<DomainValues[]>((resolve, reject) => {
      const domainValues: DomainValues[] = [];
      configurationDb.domainValues
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            domainValues.push(entry.content);
          }
          resolve(domainValues);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getDomainValue(name: string): Promise<DomainValue[]> {
    return new Promise<DomainValue[]>((resolve, reject) => {
      const domainValue: DomainValue[] = [];
      configurationDb.domainValue
        .toArray()
        .then((entries) => {
          entries.sort((a, b) => {
            if (a.content.id > b.content.id) return 1;
            if (a.content.id < b.content.id) return -1;
            return 0;
          });
          for (const entry of entries) {
            if (entry.content.domainValuesName === name) {
              domainValue.push(entry.content);
            }
          }
          resolve(domainValue);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public async getEntities(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const entities: any[] = [];
      configurationDb.entities
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            entities.push(entry.content);
          }
          resolve(entities);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getUserGroups(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const userGroups: UserGroup[] = [];
      configurationDb.userGroups
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            userGroups.push(entry.content);
          }
          resolve(userGroups);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public populateUsers(users: Array<any>): void {
    if (!users || users.length === 0) {
      this.logger.info('populateUsers - no users provided');
      return;
    }
    // get all existing to merge with provided array
    configurationDb.users
      .toArray()
      .then((entries) => {
        for (const user of users) {
          const userEntry = ConfigurationService.getCorrespondingUserEntry(user, entries);
          if (userEntry) {
            configurationDb.users
              .update(userEntry.username, {
                timestamp: Date.now(),
                content: user,
              })
              .then(() => {
                this.logger.debug('updated user', user);
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          } else {
            configurationDb.users
              .add({
                username: user.Username,
                timestamp: Date.now(),
                content: user,
              })
              .then(() => {
                this.logger.debug('added user', user);
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private static getCorrespondingUserEntry(user: any, userEntries: UserEntry[]): UserEntry | undefined {
    return userEntries.find((x) => x.username === user.Username);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public getUsersByFilter(filter: any): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const users: any[] = [];
      configurationDb.users
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            for (const attr of entry.content.Attributes) {
              if (filter.operator === 'startsWith') {
                if (
                  attr.Name === filter.field &&
                  attr.Value &&
                  filter.value &&
                  attr.Value.toLowerCase().startsWith(filter.value.toLowerCase())
                ) {
                  users.push(entry.content);
                }
              } else {
                if (attr.Name === filter.field && attr.Value === filter.value) {
                  users.push(entry.content);
                }
              }
            }
          }
          resolve(users);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
